import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, pointAnchor } from '../../ui';
import { useDeferredClose } from '../../ui/deferred-close';
import type { MenuAction, MenuItem } from './menu-items';

/**
 * Context menu at client coordinates, on the ui `Menu` (P16-2): arrows, Home and End move, Escape from anywhere and
 * a press outside close it, as does the editor on pan. The editor gives the focus back to the canvas in `onClose`,
 * which runs once the menu has animated out (P18); an item or a pan that unmounts it sooner leaves without the exit.
 */
export function ContextMenu(props: ContextMenuProps) {
  // A right click elsewhere while it is open moves the editor's menu to the new point: that is a new menu. The old one is
  // dropped with its pending `onClose` (which would clear the editor's state, and the new menu with it).
  return <PointMenu key={`${props.x},${props.y}`} {...props} />;
}

type ContextMenuProps = { items: MenuItem[]; x: number; y: number; onAction: (id: MenuAction) => void; onClose: () => void };

function PointMenu({ items, x, y, onAction, onClose }: ContextMenuProps) {
  const { t } = useTranslation();
  const anchor = useMemo(() => pointAnchor(x, y), [x, y]);
  const { open, request, complete } = useDeferredClose(onClose);
  return (
    <Menu.Root open={open} modal={false} loopFocus onOpenChange={(next) => !next && request()} onOpenChangeComplete={complete}>
      <Menu.Popup label={t('menu.label')} anchor={anchor} sideOffset={0} finalFocus={false} focusFirst className="min-w-52">
        {items.map((it, i) =>
          'separator' in it ? (
            <Menu.Separator key={`sep-${i}`} />
          ) : (
            <Menu.Item key={it.id} disabled={it.disabled} onClick={() => onAction(it.id)} className={it.danger ? 'text-danger' : undefined}>
              {t(`menu.${it.id}`)}
            </Menu.Item>
          ),
        )}
      </Menu.Popup>
    </Menu.Root>
  );
}
